import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowRight, 
  ArrowLeft, 
  BookOpen, 
  Cross, 
  Moon, 
  Sun,
  Save,
  Check,
  Trash2,
  Play,
  ExternalLink,
  Bookmark,
  BookmarkCheck,
  Clock,
  Heart,
  Music
} from 'lucide-react';
import ContentAccordion from '../components/common/ContentAccordion';
import ContentCard from '../components/common/ContentCard';
import { retreatContent } from '../data/retreatContent';

// Helper to get all days
const getAllDays = () => {
  const days = [];
  retreatContent.weeks.forEach(week => {
    week.days.forEach(day => {
      days.push({ ...day, weekNumber: week.number, pillar: week.pillar });
    });
  });
  return days;
};

const pillarColors = {
  Relationship: 'from-rose-500 to-rose-600',
  Identity: 'from-amber-500 to-amber-600',
  Mission: 'from-teal-500 to-teal-600',
  Purpose: 'from-violet-500 to-violet-600'
};

const DayPage = ({ 
  dailyNotes, 
  onSaveNote, 
  onEraseNote,
  bookmarkedDays,
  onToggleBookmark
}) => {
  const { dayNumber } = useParams();
  const navigate = useNavigate();
  const dayNum = parseInt(dayNumber);
  
  const allDays = getAllDays();
  const dayInfo = allDays.find(d => d.day === dayNum);
  
  const [noteText, setNoteText] = useState('');
  const [isSaved, setIsSaved] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Load existing note
  useEffect(() => {
    if (dailyNotes[dayNum]) {
      setNoteText(dailyNotes[dayNum]);
    } else {
      setNoteText('');
    }
    setHasChanges(false);
  }, [dayNum, dailyNotes]);

  if (!dayInfo) {
    return (
      <div className="px-4 py-8 text-center">
        <p className="text-gray-500">Day not found</p>
        <button 
          onClick={() => navigate('/')}
          className="mt-4 text-[var(--primary)] font-medium"
        >
          Return to Dashboard
        </button>
      </div>
    );
  }

  const handleSave = () => {
    onSaveNote(dayNum, noteText);
    setIsSaved(true);
    setHasChanges(false);
    setTimeout(() => setIsSaved(false), 2000);
  };

  const handleErase = () => {
    if (window.confirm('Are you sure you want to erase this reflection?')) {
      onEraseNote(dayNum);
      setNoteText('');
      setHasChanges(false);
    }
  };

  const handleNoteChange = (e) => {
    setNoteText(e.target.value);
    setHasChanges(true);
  };

  const isBookmarked = bookmarkedDays[dayNum];
  const hasPrev = dayNum > 1;
  const hasNext = dayNum < 30;
  
  // Audio file path for scripture narration
  const audioFile = `/audio/day${dayNum}.mp3`;

  return (
    <div className="px-4 py-6 max-w-2xl mx-auto space-y-6">
      {/* Day Header */}
      <div className={`bg-gradient-to-br ${pillarColors[dayInfo.pillar] || 'from-gray-500 to-gray-600'} rounded-2xl p-6 text-white`}>
        <div className="flex items-center justify-between mb-3">
          <span className="text-white/80 text-sm font-medium">
            Week {dayInfo.weekNumber} • {dayInfo.pillar}
          </span>
          <button
            onClick={() => onToggleBookmark(dayNum)}
            className="p-2 hover:bg-white/20 rounded-full transition-colors"
            title={isBookmarked ? 'Remove bookmark' : 'Bookmark this day'}
          >
            {isBookmarked ? (
              <BookmarkCheck className="w-5 h-5" />
            ) : (
              <Bookmark className="w-5 h-5" />
            )}
          </button>
        </div>
        <h1 className="font-heading text-2xl font-bold mb-2">
          Day {dayNum}: {dayInfo.title}
        </h1>
        <p className="text-white/90 text-sm">{dayInfo.scripture}</p>
        <div className="flex items-center gap-2 mt-3 text-white/70 text-xs">
          <Clock className="w-3 h-3" />
          <span>~45 min total today</span>
        </div>
      </div>

      {/* What You'll Do Today - with time estimates */}
      <div className="bg-white rounded-xl p-4 border border-gray-100">
        <h3 className="font-heading font-semibold text-[var(--primary)] mb-3">What you'll do today</h3>
        <div className="space-y-3">
          <div className="flex items-center gap-3 bg-amber-50 rounded-lg p-3 border border-amber-100">
            <BookOpen className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div className="flex-1">
              <span className="font-medium text-amber-900 text-sm">Scripture & Reflection</span>
              <span className="text-amber-600 text-xs ml-2">20-30 min</span>
            </div>
            <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">Essential</span>
          </div>
          <div className="flex items-center gap-3 bg-blue-50 rounded-lg p-3 border border-blue-100">
            <Heart className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <div className="flex-1">
              <span className="font-medium text-blue-900 text-sm">Midday Heart Check</span>
              <span className="text-blue-600 text-xs ml-2">5 min</span>
            </div>
            <span className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full">Essential</span>
          </div>
          <div className="flex items-center gap-3 bg-indigo-50 rounded-lg p-3 border border-indigo-100">
            <Moon className="w-5 h-5 text-indigo-600 flex-shrink-0" />
            <div className="flex-1">
              <span className="font-medium text-indigo-900 text-sm">Evening Examen</span>
              <span className="text-indigo-600 text-xs ml-2">10-15 min</span>
            </div>
            <span className="text-xs bg-indigo-200 text-indigo-800 px-2 py-0.5 rounded-full">Essential</span>
          </div>
          <div className="flex items-center gap-3 bg-green-50 rounded-lg p-3 border border-green-100">
            <Music className="w-5 h-5 text-green-600 flex-shrink-0" />
            <div className="flex-1">
              <span className="font-medium text-green-900 text-sm">Music & Video Talks</span>
            </div>
            <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full">Optional</span>
          </div>
        </div>
      </div>

      {/* Scripture */}
      <ContentAccordion 
        title="Scripture Passage" 
        summary={dayInfo.scriptureText?.substring(0, 100) + '...'}
        defaultOpen={true}
      >
        <p className="text-gray-700 leading-relaxed whitespace-pre-line">
          {dayInfo.scriptureText || 'Scripture text not available.'}
        </p>
        <a 
          href="https://bible.usccb.org/daily-bible-reading"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 mt-4 text-sm text-[#1B93A4] hover:underline"
        >
          <Cross className="w-4 h-4" />
          Today's Mass Readings
          <ExternalLink className="w-3 h-3" />
        </a>
      </ContentAccordion>

      {/* Reflection */}
      <ContentAccordion title="Reflection" defaultOpen={true}>
        <p className="text-gray-700 leading-relaxed">
          {dayInfo.reflection}
        </p>
      </ContentAccordion>

      {/* Audio Player for Scripture */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-200">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-md">
            <Play className="w-6 h-6 text-white ml-0.5" />
          </div>
          <div>
            <h4 className="font-heading font-semibold text-indigo-900">Listen to Scripture</h4>
            <p className="text-xs text-indigo-600">AI narration (World English Bible)</p>
          </div>
        </div>
        <audio 
          controls 
          className="w-full h-10"
          data-testid={`audio-player-day-${dayNum}`}
        >
          <source src={audioFile} type="audio/mpeg" />
          Your browser does not support audio.
        </audio>
        <p className="text-xs text-gray-500 mt-2">
          🎧 Audio uses the <a href="https://ebible.org/eng-web-c/" target="_blank" rel="noopener noreferrer" className="underline text-indigo-600 hover:text-indigo-800">World English Bible Catholic Edition (WEBC)</a> — a free, public domain translation.
        </p>
      </div>

      {/* Journal Entry */}
      <div className="bg-white rounded-xl p-4 border border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-semibold text-[var(--primary)]">Your Reflection</h3>
          {isSaved && (
            <span className="text-green-600 text-sm flex items-center gap-1">
              <Check className="w-4 h-4" /> Saved
            </span>
          )}
        </div>
        <textarea
          value={noteText}
          onChange={handleNoteChange}
          placeholder="What is God saying to you today? Write your thoughts, prayers, and insights..."
          className="w-full h-32 p-3 border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] text-gray-700"
          data-testid={`day-${dayNum}-notes`}
        />
        <div className="flex items-center justify-end gap-2 mt-3">
          {noteText.trim() && (
            <button
              onClick={handleErase}
              className="px-3 py-1.5 text-sm text-red-500 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-1"
            >
              <Trash2 className="w-4 h-4" />
              Erase
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={!hasChanges && !noteText}
            className={`px-4 py-1.5 text-sm font-medium rounded-lg flex items-center gap-1 transition-colors ${
              hasChanges 
                ? 'bg-[var(--secondary)] text-white hover:bg-[var(--secondary)]' 
                : 'bg-[var(--primary)] text-white hover:bg-[var(--primary-dark)]'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <Save className="w-4 h-4" />
            Save
          </button>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-2 gap-3">
        <ContentCard
          icon={Sun}
          title="Midday Examen"
          onClick={() => navigate('/reference/examen')}
        />
        <ContentCard
          icon={Moon}
          title="Evening Examen"
          onClick={() => navigate('/reference/examen')}
        />
      </div>

      {/* Day Navigation */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
        {hasPrev ? (
          <button
            onClick={() => navigate(`/day/${dayNum - 1}`)}
            className="flex items-center gap-2 text-gray-500 hover:text-[var(--primary)] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">Day {dayNum - 1}</span>
          </button>
        ) : (
          <div />
        )}
        
        {hasNext ? (
          <button
            onClick={() => navigate(`/day/${dayNum + 1}`)}
            className="flex items-center gap-2 bg-[var(--primary)] text-white px-4 py-2 rounded-full hover:bg-[var(--primary-dark)] transition-colors"
          >
            <span className="text-sm font-medium">Day {dayNum + 1}</span>
            <ArrowRight className="w-5 h-5" />
          </button>
        ) : (
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 bg-[var(--secondary)] text-white px-4 py-2 rounded-full hover:bg-[var(--secondary)] transition-colors"
          >
            <span className="text-sm font-medium">Complete!</span>
            <Check className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default DayPage;
